<script setup lang="ts">
  import { computed, reactive, ref, watch, watchEffect } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { Trash2 } from 'lucide-vue-next'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import { TableSchema } from '../models/TableSchema'
  import { TableField } from '../models/TableField'
  import { TableFieldType } from '../models/TableFieldType'
  import { Table } from '../models/Table'
  import { TablesService } from '../services/TablesService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import TableConfiguration from './TableConfiguration.vue'
  import { TableDataSourceType } from '../models/TableDataSourceType'
  import { AnalysisWizardModel } from '@/components/AnalysisWizard/models/AnalysisWizardModel'

  interface IArrayTableBuilderProps {
    tableSchemas: Array<TableSchema>
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    availableDataSources: Array<{
      value: string
      label: string
      id: string
      type: TableDataSourceType
    }>
    selectedDataSource?: { value: string; label: string; id: string; type: TableDataSourceType }
    tableField: TableField
    rootTable: Table | null
    modelValue: []
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
    wizardModel?: AnalysisWizardModel
  }

  interface IArrayTableBuilderEmits {
    (e: 'update:modelValue', value: Array<any>): void
    (e: 'child-table-type-changed', type: string, table: Table, configuration: {}): void
    (
      e: 'data-source-selected',
      value: { value: string; label: string; id: string; type: TableDataSourceType }
    ): void
    (e: 'generating-rows', value: boolean): void
    (e: 'row-generation-complete', success: boolean): void
  }

  const props = defineProps<IArrayTableBuilderProps>()
  const emit = defineEmits<IArrayTableBuilderEmits>()

  const arrayTableField = reactive<TableField>(props.tableField)

  if (arrayTableField.field_type !== TableFieldType.Array)
    throw new Error('ArrayTableBuilder requires an Array type TableField!')
  if (arrayTableField.item_schema == null)
    throw new Error('Array type TableFields require an item_schema!')

  const tables = ref<Array<Table>>([])

  // Drag/drop state
  const draggedIndex = ref<number | null>(null)
  const dragOverIndex = ref<number | null>(-1)
  const instanceId = `arrayTable-${Math.random().toString(36).slice(2)}`

  // Rebuild internal tables list from props.modelValue (can be invoked by parent)
  const refresh = () => {
    tables.value = []
    if (Array.isArray(props.modelValue)) {
      props.modelValue.forEach((value: any) => {
        const table = TablesService.createTableFromTableField(arrayTableField.item_schema!)
        table.configuration =
          value.configuration != null ? TablesService.getFlattenedTable(value, null) : value
        tables.value.push(table)
      })
    }
  }

  // expose for parent invocation
  defineExpose({ refresh })

  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const handleDataSourceSelected = (value: {
    value: string
    label: string
    id: string
    type: TableDataSourceType
  }) => {
    emit('data-source-selected', value)
  }

  const handleGeneratingRowsChange = (value: boolean) => {
    emit('generating-rows', value)
  }

  const handleGeneratingRowsCompleted = async (success: boolean) => {
    emit('row-generation-complete', success)
  }

  watch(
    props.modelValue,
    (newVal) => {
      tables.value = []
      if (Array.isArray(newVal)) {
        newVal.forEach((value: any) => {
          const table = TablesService.createTableFromTableField(arrayTableField.item_schema!)
          table.configuration =
            value.configuration != null ? TablesService.getFlattenedTable(value, null) : value
          tables.value.push(table)
        })
      }
    },
    { immediate: true, deep: true }
  )

  watch(
    tables,
    (newValue) => {
      emit('update:modelValue', newValue)
    },
    { deep: true }
  )

  const initializeConfiguration = (table: Table, fields: Array<TableField>) => {
    if (table == null) throw new Error('table is required')
    if (arrayTableField.item_schema?.field_type !== TableFieldType.Object) return
    const config = TablesService.initializeTableFieldConfiguration(fields)
    table.configuration = config
  }

  const isAddDisabled = computed(() => {
    const maxItems = (arrayTableField.field_type_args as any)?.array_max_items
    if (maxItems === undefined || maxItems === null) return false
    if (typeof maxItems !== 'number' || isNaN(maxItems) || maxItems < 0) return false
    return tables.value.length >= maxItems
  })

  const addTable = () => {
    const newTable = TablesService.createTableFromTableField(arrayTableField.item_schema!)
    initializeConfiguration(newTable, arrayTableField.item_schema?.fields || [])
    tables.value.push(newTable)
    message.success(`Added table: ${arrayTableField.display_name}`, defaultMessageDurationSeconds)
    return newTable
  }

  const removeTable = (index: number) => {
    tables.value.splice(index, 1)
  }

  const moveItem = (from: number, to: number) => {
    if (from === to) return
    const item = tables.value.splice(from, 1)[0]
    tables.value.splice(to, 0, item)
  }

  const moveUp = (index: number) => {
    if (index > 0) {
      moveItem(index, index - 1)
    }
  }

  const moveDown = (index: number) => {
    if (index < tables.value.length - 1) {
      moveItem(index, index + 1)
    }
  }

  const handleDragLeave = () => {
    dragOverIndex.value = null
  }

  const handleDragStart = (event: DragEvent, index: number) => {
    event.stopPropagation()
    draggedIndex.value = index
    const id = instanceId
    // store origin in dataTransfer and window fallback
    try {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
        event.dataTransfer.setData('application/x-array-builder-id', id)
      }
    } catch {}
    ;(window as any).__arrayBuilderDragSourceId = id
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) {
      dragOverIndex.value = -1
      return
    }
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    dragOverIndex.value = index
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) return

    if (draggedIndex.value == null) return
    const from = draggedIndex.value
    const to = targetIndex
    if (from === to) {
      draggedIndex.value = null
      dragOverIndex.value = -1
      ;(window as any).__arrayBuilderDragSourceId = null
      return
    }
    moveItem(from, to)
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleDragEnd = () => {
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleConfigurationUpdate = (
    table: Table,
    config: Record<string, any>,
    tableTypeChange?: boolean
  ) => {
    const tableIndex = tables.value.findIndex((t) => t.id === table.id)
    if (tableIndex > -1) {
      //tables.value[tableIndex].configuration = { ...config }

      const orig = tables.value[tableIndex] as any
      const updated = reactive({
        ...orig,
        configuration: { ...config },
        _version: (orig._version ?? 0) + 1
      })
      tables.value.splice(tableIndex, 1, updated)
    }
    if (tableTypeChange === true) {
      emit('child-table-type-changed', table.type, table, { ...config })
    }
  }
</script>

<template>
  <div
    v-if="arrayTableField.item_schema?.field_type === TableFieldType.Object"
    class="table-array-object-builder-container"
  >
    <div class="mt-[-40px] self-end flex items-center gap-2">
      <a-tooltip placement="top">
        <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
        <Button
          style="padding: 3px; border: none"
          type="text"
          size="large"
          class="flex items-center justify-center"
          @click="toggleGlobalCollapse"
          :disabled="tables.length === 0"
          :aria-disabled="tables.length === 0"
          aria-label="Toggle Collapse All"
        >
          <CompressOutlined v-if="isCollapsed" class="text-base" style="line-height: 1; margin: 0" />
          <ExpandOutlined v-else class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </a-tooltip>

      <Button
        type="primary"
        shape="circle"
        size="large"
        class="flex items-center justify-center"
        @click="addTable"
        :title="`Add ${arrayTableField.item_schema!.display_name}`"
        :disabled="isAddDisabled"
        :aria-disabled="isAddDisabled"
      >
        <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
      </Button>
    </div>

    <div class="mt-5">
      <div
        class="table-mapping-row"
        v-for="(table, index) in tables"
        :key="table.id"
        :class="{ 'drop-zone-active': dragOverIndex === index }"
        @dragover.prevent="handleDragOver($event, index)"
        @dragleave="handleDragLeave"
        @drop.prevent="handleDrop($event, index)"
      >
        <div
          class="drag-handle"
          draggable="true"
          @dragstart="handleDragStart($event, index)"
          @dragend="handleDragEnd"
          tabindex="0"
          role="button"
          :aria-grabbed="draggedIndex === index"
          aria-label="Drag to reorder"
        >
          <HolderOutlined class="text-base" style="line-height: 1; margin: 0" />
        </div>

        <TableConfiguration
          :configuration-level="props.configurationLevel + 1"
          :root-table="props.rootTable"
          :table="table"
          :available-columns="availableColumns"
          :available-params="availableParams"
          :available-data-sources="availableDataSources"
          @configuration-updated="handleConfigurationUpdate"
          @data-source-selected="handleDataSourceSelected"
          @generating-rows="handleGeneratingRowsChange"
          @row-generation-complete="handleGeneratingRowsCompleted"
          :selected-data-source="selectedDataSource"
          :table-schemas="tableSchemas"
          :is-condensed-layout="props.isCondensedLayout"
          :global-collapsed="globalCollapsed"
          :wizard-model="wizardModel"
          :current-path="
            props.currentPath
              ? [...props.currentPath, String(index)]
              : [arrayTableField.name, String(index)]
          "
        />

        <div class="action-buttons ml-2 flex flex-col items-center">
          <a-tooltip placement="left">
            <template #title>Move Up</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveUp(index)"
              :disabled="index === 0"
              :aria-disabled="index === 0"
            >
              <ArrowUpOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>{{ `Remove ${arrayTableField.item_schema!.display_name}` }}</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn ant-btn-dangerous my-1"
              @click="removeTable(index)"
            >
              <Trash2 class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>Move Down</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveDown(index)"
              :disabled="index === tables.length - 1"
              :aria-disabled="index === tables.length - 1"
            >
              <ArrowDownOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .table-array-object-builder-container {
    display: flex;
    flex-direction: column;
  }

  .table-mapping-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 15px;
  }

  .drag-handle {
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground, #6b7280);
  }

  .table-mapping-row.drop-zone-active {
    outline: 2px dashed rgba(24, 144, 255, 0.35);
    background-color: rgba(24, 144, 255, 0.03);
  }

  .action-buttons .ant-btn {
    padding: 3px;
  }

  .compact-action-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .compact-icon,
  .compact-icon svg {
    width: 14px !important;
    height: 14px !important;
    line-height: 1 !important;
  }

  select {
    padding: 4px;
  }
</style>
